import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  forceLogoutToLogin,
  isUnauthorizedApiResponse,
} from "../../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const SUCCESS_CODES = new Set([200, 201, 204]);

const parseEnvelope = async (response) => {
  const isJson = response.headers
    .get("content-type")
    ?.toLowerCase()
    .includes("application/json");
  const payload = isJson ? await response.json() : null;

  if (isUnauthorizedApiResponse(response, payload)) {
    forceLogoutToLogin();
  }

  if (!payload || !SUCCESS_CODES.has(payload.resultCode)) {
    let message =
      payload?.resultDesc ||
      (response.ok ? "Unexpected response from server." : response.statusText);

    if (payload?.errors) {
      const errorMessages = Object.values(payload.errors)
        .flat()
        .filter(Boolean);
      if (errorMessages.length) {
        message = errorMessages.join(", ");
      }
    }

    const error = new Error(message);
    error.payload = payload;
    throw error;
  }

  if (payload.data !== undefined) {
    return payload.data;
  }

  return payload;
};

const getAuthHeaders = (state) => {
  const token = state.auth?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const fetchDashboardOverview = createAsyncThunk(
  "dashboard/fetchOverview",
  async (timeRange, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const params = new URLSearchParams();
      params.append("timeRange", timeRange);
      const response = await fetch(
        `${API_BASE_URL}/dashboard/overview?${params.toString()}`,
        {
          headers: {
            Accept: "application/json",
            ...getAuthHeaders(state),
          },
        }
      );
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load dashboard.");
    }
  }
);

const initialState = {
  timeRange: "today",
  data: null,
  loading: false,
  error: null,
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    setDashboardTimeRange: (state, action) => {
      state.timeRange = action.payload || "today";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardOverview.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardOverview.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload || null;
      })
      .addCase(fetchDashboardOverview.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load dashboard.";
      });
  },
});

export const { setDashboardTimeRange } = dashboardSlice.actions;

export const selectDashboardTimeRange = (state) => state.dashboard.timeRange;
export const selectDashboardData = (state) => state.dashboard.data;
export const selectDashboardLoading = (state) => state.dashboard.loading;
export const selectDashboardError = (state) => state.dashboard.error;

export default dashboardSlice.reducer;
